import React from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Paper,
  Typography,
  Grid,
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import CustomTypography from "../../globalStyles/fonts";

const NodeEditor = ({ nodes, setNodes, nodeTypes = [] }) => {
  const addNode = () => {
    const newNode = {
      id: `node_${Date.now()}`,
      label: "",
      type: "default",
      column: 0,
      row: 0,
      color: "#4A90E2",
      shape: "box",
      parentId: null,
    };
    setNodes([...nodes, newNode]);
  };

  const updateNode = (id, field, value) => {
    setNodes(
      nodes.map((node) =>
        node.id === id ? { ...node, [field]: value } : node,
      ),
    );
  };

  const deleteNode = (id) => {
    setNodes(nodes.filter((node) => node.id !== id));
  };

  const nodeShapes = ["box", "circle", "ellipse", "database", "diamond"];
  const predefinedColors = [
    { label: "Blue", value: "#4A90E2" },
    { label: "Green", value: "#018749" },
    { label: "Orange", value: "#FF9800" },
    { label: "Red", value: "#FF6347" },
    { label: "Purple", value: "#9C27B0" },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <CustomTypography variant="h6">Nodes</CustomTypography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={addNode}
          size="small"
        >
          Add Node
        </Button>
      </Box>

      <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
        {nodes.map((node, index) => (
          <Paper key={node.id} sx={{ p: 2, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">Node {index + 1}</Typography>
              <IconButton
                size="small"
                color="error"
                onClick={() => deleteNode(node.id)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Label"
                  value={node.label}
                  onChange={(e) => updateNode(node.id, "label", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Type"
                  value={node.type}
                  onChange={(e) => updateNode(node.id, "type", e.target.value)}
                  placeholder="e.g., Supplier, Warehouse, Consumer"
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Column"
                  type="number"
                  value={node.column}
                  onChange={(e) =>
                    updateNode(node.id, "column", parseInt(e.target.value))
                  }
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Row"
                  type="number"
                  value={node.row}
                  onChange={(e) =>
                    updateNode(node.id, "row", parseInt(e.target.value))
                  }
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Shape</InputLabel>
                  <Select
                    value={node.shape}
                    label="Shape"
                    onChange={(e) =>
                      updateNode(node.id, "shape", e.target.value)
                    }
                  >
                    {nodeShapes.map((shape) => (
                      <MenuItem key={shape} value={shape}>
                        {shape}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Color</InputLabel>
                  <Select
                    value={node.color}
                    label="Color"
                    onChange={(e) =>
                      updateNode(node.id, "color", e.target.value)
                    }
                  >
                    {predefinedColors.map((color) => (
                      <MenuItem key={color.value} value={color.value}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              bgcolor: color.value,
                              mr: 1,
                              border: "1px solid var(--border-color)",
                            }}
                          />
                          {color.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Parent Node</InputLabel>
                  <Select
                    value={node.parentId || ""}
                    label="Parent Node"
                    onChange={(e) =>
                      updateNode(
                        node.id,
                        "parentId",
                        e.target.value === "" ? null : e.target.value,
                      )
                    }
                  >
                    <MenuItem value="">None (Root Node)</MenuItem>
                    {nodes
                      .filter((n) => n.id !== node.id)
                      .map((n) => (
                        <MenuItem key={n.id} value={n.id}>
                          {n.label || n.id}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        ))}

        {nodes.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="textSecondary">
              No nodes added yet. Click "Add Node" to get started.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NodeEditor;
